/**
 * Recharge Storefront Experiences — pointer-event + scroll-lock coordination
 *
 * External theme CSS cannot reach nodes inside Declarative Shadow Roots. Recharge mounts
 * `#storefront-portal` before the cart sheet; when that portal is empty it can still paint
 * above sibling layers and swallow every click (cart UI, backdrop dismiss, page underneath).
 *
 * Pebble cart drawer uses `<dialog scroll-lock>` → `html.scroll-locked` + Lenis `lenis-stopped`.
 * If the theme cart stays `showModal()` while Recharge’s `#CartSheet` opens, the native dialog
 * top layer can grab events and leave the page feeling frozen — close the theme drawer when
 * Recharge’s cart sheet is open.
 *
 * This script: (1) pointer-events:none on empty `#storefront-portal` inside known hosts;
 * (2) reinforces pointer-events:none on the cart-overlay light-DOM host while aria-hidden;
 * (3) closes `#CartDrawer` when Recharge’s cart sheet is `[data-state=open]`;
 * (4) optional cart theme bridge when `<html data-recharge-cart-theme-match>` — shadow injection
 *     with theme CSS variables + Pebble-like primary CTAs (fonts, `.btn` geometry, hover wipe);
 *     sheet shell still mirrors resolved colours/fonts from `#cart-drawer-dialog` when present.
 */
(function () {
  "use strict";

  const SEL = {
    root: "#recharge-storefront-experiences",
    drawer: "#recharge-storefront-experiences__cart-drawer",
    overlayHost: "#recharge-storefront-experiences__cart-overlay",
  };

  const CART_THEME_BRIDGE_ID = "raindrop-recharge-cart-theme-bridge";

  /**
   * !important + shadow selectors — Recharge uses `#CartSheet` > scrim + `[role=dialog]`, inner `#cart-drawer`,
   * and Tailwind utilities `*-storefront-drawer-*` + config-driven inline styles. Checkout is often
   * `button[type=submit][name=checkout]`, not `/checkout` links.
   */
  var CART_THEME_BRIDGE_CSS =
    ":host{color:rgb(var(--color-foreground))!important;font-family:var(--font-body--family)!important;font-weight:var(--font-body--weight)!important;font-style:var(--font-body--style)!important;}" +
    ":is(#CartSheet,#cart-sheet){color:rgb(var(--color-foreground))!important;font-family:var(--font-body--family)!important;font-weight:var(--font-body--weight)!important;background-color:rgb(var(--color-background))!important;}" +
    ":is(#CartSheet,#cart-sheet) #cart-drawer{font-family:var(--font-body--family)!important;background-color:rgb(var(--color-background))!important;color:rgb(var(--color-foreground))!important;}" +
    ":is(#CartSheet,#cart-sheet) #cart-drawer :where(h1,h2,h3,h4,h5,h6){font-family:var(--font-heading--family)!important;font-weight:var(--font-heading--weight)!important;font-style:var(--font-heading--style)!important;}" +
    ":is(#CartSheet,#cart-sheet) :where(input,select,textarea){font-family:var(--font-body--family)!important;font-size:var(--font-size--paragraph)!important;}" +
    ":is(#CartSheet,#cart-sheet) [class~=\"text-storefront-drawer-primary-foreground\"]{color:rgb(var(--color-primary-button-text))!important;}" +
    ":is(#CartSheet,#cart-sheet) [class*='bg-storefront-drawer-background']{background-color:rgb(var(--color-background))!important;}" +
    ":is(#CartSheet,#cart-sheet) [class*='text-storefront-drawer-foreground']:not(.dynamic-richtext){color:rgb(var(--color-foreground))!important;}" +
    ":is(#CartSheet,#cart-sheet) .dynamic-richtext{font-size:0.875rem!important;line-height:1.5!important;}" +
    ":is(#CartSheet,#cart-sheet) .dynamic-richtext p,:is(#CartSheet,#cart-sheet) .dynamic-richtext strong{font-size:inherit!important;line-height:inherit!important;}" +
    ":is(#CartSheet,#cart-sheet) [class*='text-storefront-drawer-muted']{color:var(--color-foreground-secondary)!important;}" +
    ":is(#CartSheet,#cart-sheet) [class*='text-storefront-drawer-accent']{color:rgb(var(--color-sale-price))!important;}" +
    ":is(#CartSheet,#cart-sheet) [class*='border-storefront-drawer-border']{border-color:rgb(var(--color-border)/var(--color-border-alpha,1))!important;}" +
    ":is(#CartSheet,#cart-sheet) footer{background-color:rgb(var(--color-background))!important;}" +
    ":is(#CartSheet,#cart-sheet) :where(hr,[role='separator']){border-color:rgb(var(--color-border)/var(--color-border-alpha,1))!important;}";

  /** Matches theme `.btn` / `.btn--primary` + hover wipe (::before); keep out of inline styles so hover works. */
  var RS_RECHARGE_PRIMARY_CTA =
    'button[type="submit"][name="checkout"],a[href*="/checkout"],button[class~="bg-storefront-drawer-primary"]';
  var CART_THEME_BRIDGE_PRIMARY_BTN_CSS =
    ":is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    "){display:inline-flex!important;align-items:center!important;justify-content:center!important;gap:0.8rem!important;position:relative!important;z-index:0!important;overflow:hidden!important;white-space:nowrap!important;text-overflow:ellipsis!important;text-decoration:none!important;height:var(--button-height,5rem)!important;width:auto!important;padding:var(--button-padding-block,1rem) var(--button-padding-inline,2.4rem)!important;line-height:var(--button-line-height,var(--font-paragraph--line-height,1.6))!important;border-radius:var(--button-corner-radius,0)!important;border-width:var(--button-border-thickness,0.1rem)!important;border-style:var(--button-border-style,solid)!important;font-size:var(--font-size--paragraph)!important;cursor:pointer!important;font-family:var(--font-button--family)!important;font-weight:var(--font-button--weight)!important;font-style:var(--font-button--style)!important;letter-spacing:var(--font-button--letter-spacing)!important;text-transform:var(--font-button--transform)!important;transition:background-color,color,border-color,opacity,transform var(--animation-duration--medium)!important;background-color:rgb(var(--color-primary-button-background))!important;color:rgb(var(--color-primary-button-text))!important;border-color:rgb(var(--color-primary-button-background))!important;}" +
    ":is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    ')::before{content:""!important;position:absolute!important;width:0!important;inset-block:calc(var(--button-border-thickness,0.1rem)*-1)!important;inset-inline-start:calc(var(--button-border-thickness,0.1rem)*-1)!important;z-index:-1!important;transition:width 0.3s!important;border-radius:var(--button-corner-radius,0)!important;background-color:rgb(var(--color-primary-button-text))!important;}' +
    ":is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    "):disabled,:is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    ')[aria-disabled="true"]{opacity:0.5!important;cursor:not-allowed!important;}' +
    "@media (hover:hover) and (pointer:fine){:is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    "):hover::before{width:calc(100% + var(--button-border-thickness,0.1rem)*2)!important;}:is(#CartSheet,#cart-sheet) :where(" +
    RS_RECHARGE_PRIMARY_CTA +
    "):hover{color:rgb(var(--color-primary-button-background))!important;}}";

  /** Sheet root inside the drawer shadow (id may differ by Recharge version). */
  function queryCartSheet(shadowRoot) {
    if (!shadowRoot) return null;
    var el = shadowRoot.querySelector("#CartSheet");
    if (el) return el;
    el = shadowRoot.querySelector("#cart-sheet");
    if (el) return el;
    var nodes = shadowRoot.querySelectorAll("[id]");
    for (var i = 0; i < nodes.length; i++) {
      if (/cartsheet/i.test(nodes[i].id)) return nodes[i];
    }
    return null;
  }

  const watchedShadows = new WeakSet();
  var applyTokensScheduled = false;

  function cartThemeMatchEnabled() {
    if (window.__RD_RECHARGE_THEME_MATCH__ === true) return true;
    if (document.documentElement.hasAttribute("data-recharge-cart-theme-match")) return true;
    try {
      return !!(
        window.FoxTheme &&
        window.FoxTheme.settings &&
        window.FoxTheme.settings.rechargeCartMatchTheme
      );
    } catch (e) {
      return false;
    }
  }

  /** Host id can vary slightly by app version — prefer canonical id, then suffix match under root. */
  function getCartDrawerHost() {
    var byId = document.querySelector(SEL.drawer);
    if (byId) return byId;
    var root = document.querySelector(SEL.root);
    if (!root) return null;
    return root.querySelector("[id$='cart-drawer'], [id*='__cart-drawer']");
  }

  function isCartDrawerShadowHost(hostEl) {
    if (!hostEl || !hostEl.id) return false;
    return hostEl.id.indexOf("__cart-drawer") !== -1;
  }

  function getThemeCartDialogEl() {
    return document.getElementById("cart-drawer-dialog");
  }

  function applyResolvedTokensToCartSheet(shadowRoot) {
    if (!cartThemeMatchEnabled() || !shadowRoot) return;

    var cartSheet = queryCartSheet(shadowRoot);
    if (!cartSheet) return;

    var dialog = getThemeCartDialogEl();
    var innerDrawer = cartSheet.querySelector("#cart-drawer");

    if (dialog) {
      var d = getComputedStyle(dialog);
      cartSheet.style.setProperty("background-color", d.backgroundColor, "important");
      cartSheet.style.setProperty("color", d.color, "important");
      cartSheet.style.setProperty("font-family", d.fontFamily, "important");
      cartSheet.style.setProperty("font-weight", d.fontWeight, "important");
      cartSheet.style.setProperty("font-style", d.fontStyle, "important");
      if (innerDrawer) {
        innerDrawer.style.setProperty("background-color", d.backgroundColor, "important");
        innerDrawer.style.setProperty("color", d.color, "important");
        innerDrawer.style.setProperty("font-family", d.fontFamily, "important");
      }
    } else {
      /* Cart template / edge cases: theme drawer dialog may not render — use document surface */
      var fallback = getComputedStyle(document.body || document.documentElement);
      cartSheet.style.setProperty("background-color", fallback.backgroundColor, "important");
      cartSheet.style.setProperty("color", fallback.color, "important");
      cartSheet.style.setProperty("font-family", fallback.fontFamily, "important");
      if (innerDrawer) {
        innerDrawer.style.setProperty("background-color", fallback.backgroundColor, "important");
        innerDrawer.style.setProperty("color", fallback.color, "important");
      }
    }

  }

  function scheduleApplyResolvedTokens(shadowRoot) {
    if (!shadowRoot) return;
    if (applyTokensScheduled) return;
    applyTokensScheduled = true;
    requestAnimationFrame(function () {
      applyTokensScheduled = false;
      applyResolvedTokensToCartSheet(shadowRoot);
    });
  }

  function injectCartThemeBridge(hostEl) {
    if (!cartThemeMatchEnabled()) return;
    if (!hostEl || !hostEl.shadowRoot) return;
    if (!isCartDrawerShadowHost(hostEl)) return;
    var sr = hostEl.shadowRoot;

    /**
     * Recharge’s module (`storefront-experiences.js`) may hydrate after this script and replace
     * shadow subtree — reinject if missing and keep our sheet **last** so !important wins over
     * Tailwind layers appended earlier in the same shadow root.
     */
    var bridge = sr.getElementById(CART_THEME_BRIDGE_ID);
    if (!bridge) {
      bridge = document.createElement("style");
      bridge.id = CART_THEME_BRIDGE_ID;
      bridge.setAttribute("data-raindrop-recharge-bridge", "");
    }
    bridge.textContent = CART_THEME_BRIDGE_CSS + CART_THEME_BRIDGE_PRIMARY_BTN_CSS;
    if (!bridge.parentNode) {
      sr.appendChild(bridge);
    } else if (sr.lastElementChild !== bridge) {
      /* Keep bridge last for cascade — but skip if already last or appendChild loops MutationObserver → freeze */
      sr.appendChild(bridge);
    }

    scheduleApplyResolvedTokens(sr);
  }

  function isOverlayHidden(host) {
    if (!host) return false;
    return (
      host.getAttribute("aria-hidden") === "true" ||
      host.getAttribute("data-aria-hidden") === "true"
    );
  }

  function patchPortal(shadowRoot) {
    if (!shadowRoot) return;
    var portal = shadowRoot.querySelector("#storefront-portal");
    if (!portal) return;

    var empty =
      portal.childElementCount === 0 &&
      (!portal.textContent || portal.textContent.trim() === "");

    if (empty) {
      portal.style.setProperty("pointer-events", "none", "important");
    } else {
      portal.style.removeProperty("pointer-events");
    }
  }

  function syncOverlayLightDom() {
    var overlay = document.querySelector(SEL.overlayHost);
    if (!overlay) return;

    if (isOverlayHidden(overlay)) {
      overlay.style.setProperty("pointer-events", "none", "important");
    } else {
      overlay.style.removeProperty("pointer-events");
    }
  }

  function isRechargeCartSheetOpen() {
    var host = getCartDrawerHost();
    if (!host || !host.shadowRoot) return false;
    var sheet = queryCartSheet(host.shadowRoot);
    return !!(sheet && sheet.getAttribute("data-state") === "open");
  }

  /** Close Pebble cart drawer via its component API so `scroll-locked` / lockDialogCount stay correct */
  function closeThemeCartDrawerIfRechargeOpen() {
    if (!isRechargeCartSheetOpen()) return;
    var cart = document.getElementById("CartDrawer");
    if (!cart || typeof cart.close !== "function") return;
    try {
      if (cart.refs && cart.refs.dialog && cart.refs.dialog.open) {
        cart.close();
      }
    } catch (_) {}
  }

  function watchShadowHost(hostEl) {
    if (!hostEl || !hostEl.shadowRoot) return;
    var sr = hostEl.shadowRoot;
    patchPortal(sr);
    injectCartThemeBridge(hostEl);

    if (watchedShadows.has(sr)) return;
    watchedShadows.add(sr);
    var shadowSyncScheduled = null;
    new MutationObserver(function () {
      if (shadowSyncScheduled !== null) return;
      shadowSyncScheduled = requestAnimationFrame(function () {
        shadowSyncScheduled = null;
        patchPortal(sr);
        injectCartThemeBridge(hostEl);
        scheduleApplyResolvedTokens(sr);
        closeThemeCartDrawerIfRechargeOpen();
      });
    }).observe(sr, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    });
  }

  function syncAll() {
    syncOverlayLightDom();
    watchShadowHost(getCartDrawerHost());
    watchShadowHost(document.querySelector(SEL.overlayHost));
    closeThemeCartDrawerIfRechargeOpen();
  }

  var rootSyncTimer = null;
  function debouncedSyncAllFromRoot() {
    if (rootSyncTimer !== null) clearTimeout(rootSyncTimer);
    rootSyncTimer = setTimeout(function () {
      rootSyncTimer = null;
      syncAll();
    }, 120);
  }

  function observeRechargeRoot(root) {
    syncAll();
    new MutationObserver(debouncedSyncAllFromRoot).observe(root, {
      attributes: true,
      attributeFilter: ["aria-hidden", "data-aria-hidden"],
      childList: true,
      subtree: true,
    });
  }

  function boot() {
    var root = document.querySelector(SEL.root);
    if (root) {
      observeRechargeRoot(root);
      return;
    }

    var mo = new MutationObserver(function () {
      root = document.querySelector(SEL.root);
      if (root) {
        mo.disconnect();
        observeRechargeRoot(root);
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.addEventListener("load", function () {
    syncAll();
  });

  /**
   * Light follow-ups only — tight intervals (250ms × 120) blocked the main thread / triggered
   * Chrome “page unresponsive”. One slow sweep is enough for late-mounted drawer hosts.
   */
  var hydrateTicks = 0;
  var hydrateIv = setInterval(function () {
    syncAll();
    if (++hydrateTicks >= 45) clearInterval(hydrateIv);
  }, 1000);

  /** Theme-match only: occasional reinject if Recharge drops the bridge style (no spam). */
  var themePollTicks = 0;
  var themePollIv = setInterval(function () {
    if (!cartThemeMatchEnabled()) {
      clearInterval(themePollIv);
      return;
    }
    syncAll();
    if (++themePollTicks >= 25) clearInterval(themePollIv);
  }, 2000);
})();
